import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function CampingCanadianRockiesArticle() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "camping-in-the-canadian-rockies";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "lake-louise-campground": "Lake Louise Campground",
    "two-jack-lakeside-campground": "Two Jack Lakeside",
    "whistlers-campground": "Whistlers Campground",
    "kicking-horse-campground": "Kicking Horse Campground",
    "redstreak-campground": "Redstreak Campground",
    "townsite-campground": "Townsite Campground",
    "elkwood-campground": "Elkwood Campground",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://parks.canada.ca/",
      label: "Parks Canada Official Site",
    },
    {
      url: "https://reservation.pc.gc.ca/",
      label: "Parks Canada Reservation Service",
    },
    {
      url: "https://www.albertaparks.ca/",
      label: "Alberta Parks – Camping Information",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Coordinates from the conversation (rounded to 5 decimals).
  const locations = [
    {
      id: "lake-louise-campground",
      lat: 51.41764,
      lng: -116.17340,
      popupText: "Lake Louise Campground (Banff NP)",
    },
    {
      id: "two-jack-lakeside-campground",
      lat: 51.22490,
      lng: -115.50200,
      popupText: "Two Jack Lakeside Campground (Banff NP)",
    },
    {
      id: "whistlers-campground",
      lat: 52.84664,
      lng: -118.07845,
      popupText: "Whistlers Campground (Jasper NP)",
    },
    {
      id: "kicking-horse-campground",
      lat: 51.42287,
      lng: -116.43526,
      popupText: "Kicking Horse Campground (Yoho NP)",
    },
    {
      id: "redstreak-campground",
      lat: 50.62490,
      lng: -116.06003,
      popupText: "Redstreak Campground (Kootenay NP)",
    },
    {
      id: "townsite-campground",
      lat: 49.04875,
      lng: -113.90934,
      popupText: "Townsite Campground (Waterton Lakes NP)",
    },
    {
      id: "elkwood-campground",
      lat: 50.66225,
      lng: -115.11336,
      popupText: "Elkwood Campground (Peter Lougheed PP)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Ultimate Guide to Camping in the Canadian Rockies</title>
        <meta
          name="description"
          content="A detailed guide covering the best campgrounds in the Canadian Rockies, tips, local regulations, and FAQs."
        />
        <meta
          name="keywords"
          content="camping, canadian rockies, banff, jasper, national parks"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/camping-in-the-canadian-rockies.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Ultimate Guide to Camping in the Canadian Rockies"
        />
        <meta
          property="og:description"
          content="Explore Banff, Jasper, Yoho, and more with tips, best campgrounds, and crucial safety guidelines."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/camping-in-the-canadian-rockies.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/camping-in-the-canadian-rockies.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Ultimate Guide to Camping in the Canadian Rockies"
        />
        <meta
          name="twitter:description"
          content="Detailed guide on front-country camping spots, local regulations, and tips in Canada's Rocky Mountains."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/camping-in-the-canadian-rockies.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Camping in the Canadian Rockies"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Camping in the Canadian Rockies is a bucket-list experience that
              places you in the heart of some of the world’s most dramatic
              mountain landscapes. Encompassing iconic parks like Banff, Jasper,
              Yoho, and Kootenay, the region offers turquoise lakes, towering
              peaks, and abundant wildlife encounters. This guide will walk you
              through the best campgrounds, practical tips, local regulations,
              and frequently asked questions to make your Rockies camping trip
              unforgettable.
            </p>

            {/* SECTION: Lake Louise Campground */}
            <h2 id="lake-louise-campground">Lake Louise Campground</h2>
            <p
              ref={(el) => setSpotRef("lake-louise-campground", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Lake Louise Campground</strong>{" "}
              <Marker
                coordY={51.41764}
                coordX={-116.17340}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Nestled in Banff National Park, Lake Louise Campground is split
              into separate loops for tents and hard-sided RVs, with an
              electrified fence around the tent area for bear safety. This
              campground is extremely popular for its proximity to the famous
              turquoise waters of Lake Louise and Moraine Lake. Amenities
              include hot showers, flush toilets, fire pits, and easy access to
              nearby trails. Reservations are highly recommended during peak
              summer months.
            </p>

            {/* SECTION: Two Jack Lakeside Campground */}
            <h2 id="two-jack-lakeside-campground">Two Jack Lakeside Campground</h2>
            <p
              ref={(el) => setSpotRef("two-jack-lakeside-campground", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Two Jack Lakeside Campground</strong>{" "}
              <Marker
                coordY={51.22490}
                coordX={-115.50200}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Also located in Banff National Park, Two Jack Lakeside is known for
              its serene waterfront sites and stunning mountain views. Ideal for
              small RVs and tents, this campground provides a peaceful getaway
              from the busier areas near Banff town. There's potable water,
              fire pits, and basic facilities (showers are available at the
              nearby Two Jack Main). Reservations fill up fast due to its
              desirable location on the shores of Two Jack Lake.
            </p>

            {/* SECTION: Whistlers Campground */}
            <h2 id="whistlers-campground">Whistlers Campground</h2>
            <p
              ref={(el) => setSpotRef("whistlers-campground", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Whistlers Campground</strong>{" "}
              <Marker
                coordY={52.84664}
                coordX={-118.07845}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              As Jasper National Park’s largest campground, Whistlers boasts
              recently upgraded amenities, from heated washrooms to updated
              sites suitable for any camping setup. Frequent wildlife visits,
              especially elk, make this a quintessential Jasper experience,
              though campers should keep a safe distance. Its prime location
              near Jasper town provides convenient access to shops, trails, and
              the world-renowned Dark Sky Preserve for nighttime stargazing.
            </p>

            {/* SECTION: Kicking Horse Campground */}
            <h2 id="kicking-horse-campground">Kicking Horse Campground</h2>
            <p
              ref={(el) => setSpotRef("kicking-horse-campground", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Kicking Horse Campground</strong>{" "}
              <Marker
                coordY={51.42287}
                coordX={-116.43526}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Located in Yoho National Park near the village of Field, Kicking
              Horse Campground is surrounded by lush forest and the Kicking
              Horse River. A short drive away are the impressive Takakkaw Falls,
              one of Canada’s tallest waterfalls, and the famous Spiral Tunnels
              viewpoint. While train whistles can echo at night, the beautiful
              scenery and easy access to trails more than compensate. Amenities
              include flush toilets, hot showers, and fire pits.
            </p>

            {/* SECTION: Redstreak Campground */}
            <h2 id="redstreak-campground">Redstreak Campground</h2>
            <p
              ref={(el) => setSpotRef("redstreak-campground", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Redstreak Campground</strong>{" "}
              <Marker
                coordY={50.62490}
                coordX={-116.06003}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Perched above Radium Hot Springs in Kootenay National Park,
              Redstreak Campground features extensive loops for tents and RVs.
              Bighorn sheep are frequent visitors, and a short hike leads you
              down to the mineral hot spring pools. The area often enjoys warmer
              weather, giving you a comfortable camping experience with a scenic
              view over the Columbia Valley.
            </p>

            {/* SECTION: Townsite Campground (Waterton) */}
            <h2 id="townsite-campground">Townsite Campground</h2>
            <p
              ref={(el) => setSpotRef("townsite-campground", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Townsite Campground</strong>{" "}
              <Marker
                coordY={49.04875}
                coordX={-113.90934}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Waterton Lakes National Park’s Townsite Campground offers the
              unique experience of camping right in the charming Waterton town.
              You’ll enjoy direct access to shops, restaurants, and lake
              activities, though it can be quite windy. Most sites are open and
              grassy, and there are communal kitchen shelters. This is an ideal
              choice for those who want a balance of nature and convenient
              in-town amenities.
            </p>

            {/* SECTION: Elkwood Campground */}
            <h2 id="elkwood-campground">Elkwood Campground</h2>
            <p
              ref={(el) => setSpotRef("elkwood-campground", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Elkwood Campground</strong>{" "}
              <Marker
                coordY={50.66225}
                coordX={-115.11336}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Tucked in Peter Lougheed Provincial Park in Kananaskis Country,
              Elkwood Campground is known for its forested sites, modern
              washrooms with hot showers, and proximity to both Upper and Lower
              Kananaskis Lakes. It’s a perfect base for hiking, fishing, and
              canoeing. A Kananaskis Conservation Pass is required to enter this
              area, and bear-safe practices are essential.
            </p>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              When camping in the Canadian Rockies, you’ll need a National Park
              Entry Pass or provincial pass (for places like Kananaskis Country)
              alongside your campsite reservation. Always store food in
              bear-proof lockers or secured vehicles, never leave trash or
              cooking gear out, and follow the “bare campsite” rules. Keep a
              respectful distance from wildlife—100 meters from bears, 30 meters
              from deer or elk—and carry bear spray if hiking. Obey fire bans,
              obtain a fire permit where required, and fully extinguish
              campfires. Check the latest information on weather, trail
              conditions, and any wildfire or wildlife advisories from official
              park websites before heading out.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. When is the best time to camp in the Canadian Rockies?</h3>
              <p>
                The prime camping season generally runs from late June to early
                September for warm weather and fully open facilities. July and
                August are busiest, so reserve well in advance. Shoulder seasons
                (May–June and September–October) see fewer crowds but cooler
                temperatures.
              </p>

              <h3>2. Do I need reservations for these campgrounds?</h3>
              <p>
                Yes. Popular campgrounds like Lake Louise, Two Jack Lakeside,
                and Whistlers fill up quickly. Use the Parks Canada Reservation
                Service for national parks or Alberta Parks for Kananaskis.
                Booking opens in early spring, and it’s best to secure your site
                as soon as reservations go live.
              </p>

              <h3>3. How do I stay safe around wildlife?</h3>
              <p>
                Always keep a clean campsite. Never feed or approach wild
                animals. Carry bear spray if hiking, remain alert, make noise on
                trails, and follow posted advisories. Store all food and scented
                items in your vehicle or designated bear lockers to minimize
                encounters.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Camping in the Canadian Rockies is an incredible way to immerse
              yourself in nature’s grandeur. From Banff’s iconic turquoise lakes
              to Jasper’s starry skies, these campgrounds each provide unique
              amenities and stunning settings. By understanding local
              regulations, making early reservations, and practicing
              wildlife-safe habits, you’ll be set for an unforgettable adventure
              among Canada’s most spectacular peaks.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}